import { useCallback } from 'react';

export type UseCsvDownloadOptions = {
  defaultFilename?: string;
  withBom?: boolean;
  mimeType?: string;
};

export const makeCsvBlob = (
  csv: string,
  withBom = true,
  mime = 'text/csv;charset=utf-8'
): Blob => {
  const BOM = '\uFEFF';
  const data = withBom ? BOM + csv : csv;
  return new Blob([data], { type: mime });
};

export const triggerBrowserDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.rel = 'noopener';

  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const ensureCsvExtension = (name: string): string => {
  return name.toLowerCase().endsWith('.csv') ? name : `${name}.csv`;
};

export const useCsvDownload = (
  opts: UseCsvDownloadOptions = {}
): { downloadCsv: (csvString: string, filename?: string) => void } => {
  const {
    defaultFilename = 'data.csv',
    withBom = true,
    mimeType = 'text/csv;charset=utf-8'
  } = opts;

  const downloadCsv = useCallback(
    (csvString: string, filename?: string) => {
      const name = filename ?? defaultFilename;
      const finalName = ensureCsvExtension(name);

      const blob = makeCsvBlob(csvString, withBom, mimeType);
      triggerBrowserDownload(blob, finalName);
    },
    [defaultFilename, withBom, mimeType]
  );

  return { downloadCsv };
};
